import React, { memo, useMemo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { formatResponseTime } from '../utils/noteHelpers';

/**
 * SessionCompleteOverlay - Shows session performance summary before navigation
 * 
 * Design variants:
 * - Notes mastered: Mastery illustration + "You Achieved Mastery!" + mastered notes list
 * - 100% accuracy (no mastery): Trophy illustration + "Outstanding Performance!"
 * - 80-99% accuracy: Stars illustration + "Great Job!"
 * - Under 80%: No illustration, encouraging message
 */
const SessionCompleteOverlay = memo(function SessionCompleteOverlay({ 
  isVisible, 
  metrics,
  mode,
  masteredNotes = [],
  onContinue
}) {
  
  const hasMastery = masteredNotes && masteredNotes.length > 0;
  
  const performanceLevel = useMemo(() => {
    if (!metrics) return 'needsWork';
    if (hasMastery) return 'mastery';
    if (metrics.accuracy === 100) return 'perfect';
    if (metrics.accuracy >= 80) return 'great';
    return 'needsWork';
  }, [metrics, hasMastery]);
  
  const performanceConfig = {
    mastery: {
      title: 'You Achieved Mastery!',
      illustration: '/mastery-illustration.png',
      showIllustration: true,
      showMasteredNotes: true
    },
    perfect: {
      title: 'Outstanding Performance!',
      illustration: '/trophy.png',
      showIllustration: true,
      showMasteredNotes: false
    },
    great: {
      title: 'Great Job!',
      illustration: '/stars.png',
      showIllustration: true,
      showMasteredNotes: false
    },
    needsWork: {
      title: 'Keep Practicing!',
      subtitle: "Every session makes you stronger. You've got this!",
      illustration: null,
      showIllustration: false,
      showMasteredNotes: false
    }
  };
  
  const config = performanceConfig[performanceLevel];
  
  // Format note name with subscript octave (e.g., C4 -> C₄)
  const formatNoteWithOctave = (note) => {
    const letter = note.slice(0, -1);
    const octave = note.slice(-1);
    return { letter, octave };
  };
  
  if (!isVisible || !metrics) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-6"
      style={{ backgroundColor: 'rgba(80, 76, 72, 0.85)' }}
      data-testid="session-complete-overlay"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className="w-full max-w-sm bg-cream-100 rounded-popup shadow-2xl overflow-hidden"
      >
        {/* Illustration Section */}
        {config.showIllustration && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
            className="flex justify-center pt-8 pb-2"
          >
            <img 
              src={config.illustration} 
              alt={hasMastery ? 'Mastery celebration' : performanceLevel === 'perfect' ? 'Trophy' : 'Stars'} 
              className={hasMastery ? "w-40 h-40 object-contain" : "w-48 h-48 object-contain"}
            />
          </motion.div>
        )}
        
        {/* Title Section */}
        <div className={`px-6 ${config.showIllustration ? 'pt-2 pb-4' : 'pt-10 pb-6'} text-center`}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="font-serif text-2xl text-cream-900 mb-1">
              {config.title}
            </h2>
            {config.subtitle && (
              <p className="text-sm text-wood-600 font-sans px-4">
                {config.subtitle}
              </p>
            )}
          </motion.div>
        </div>
        
        {/* Mastered Notes Display (instead of accuracy %) */}
        {config.showMasteredNotes && masteredNotes.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center pb-6 px-6"
          >
            <div className="flex justify-center items-baseline gap-4 flex-wrap">
              {masteredNotes.map((note, index) => {
                const { letter, octave } = formatNoteWithOctave(note);
                return (
                  <motion.div
                    key={note}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="inline-flex items-baseline"
                  >
                    <span className="font-serif text-5xl text-cream-900 font-medium">
                      {letter}
                    </span>
                    <span className="text-2xl text-cream-800 -ml-0.5 align-sub">
                      {octave}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        ) : (
          /* Large Accuracy Display (when no mastery) */
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center pb-6"
          >
            <div className="inline-flex items-baseline">
              <span className="font-serif text-6xl text-cream-900 font-medium">
                {metrics.accuracy}
              </span>
              <span className="text-3xl text-wood-500 ml-1">%</span>
            </div>
          </motion.div>
        )}
        
        {/* Stats Row */}
        <div className="px-6 pb-6">
          <div className="flex justify-center items-start gap-8">
            {/* Correct */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-center"
            >
              <div className="flex items-center justify-center mb-1">
                <CheckCircle2 className="w-5 h-5 text-sage-500" strokeWidth={1.5} />
              </div>
              <span className="font-serif text-3xl text-cream-900 block">
                {metrics.correctCount}
              </span>
              <p className="text-xs uppercase tracking-widest text-wood-500 mt-1">Correct</p>
            </motion.div>
            
            {/* Missed */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <div className="flex items-center justify-center mb-1">
                <XCircle className="w-5 h-5 text-coral-400" strokeWidth={1.5} />
              </div>
              <span className="font-serif text-3xl text-cream-900 block">
                {metrics.incorrectCount}
              </span>
              <p className="text-xs uppercase tracking-widest text-wood-500 mt-1">Missed</p>
            </motion.div>
            
            {/* Avg Time */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-center"
            >
              <div className="flex items-center justify-center mb-1">
                <Clock className="w-5 h-5 text-dusty-500" strokeWidth={1.5} />
              </div>
              <span className="font-serif text-3xl text-cream-900 block">
                {formatResponseTime(metrics.avgResponseTime)}
              </span>
              <p className="text-xs uppercase tracking-widest text-wood-500 mt-1">Avg Time</p>
            </motion.div>
          </div>
        </div>
        
        {/* Continue Button */}
        <div className="px-6 pb-6">
          <Button
            onClick={onContinue}
            className="w-full bg-coral-500 hover:bg-coral-600 active:bg-coral-700 text-white font-sans font-medium py-6 rounded-btn text-lg transition-colors"
            data-testid="continue-btn"
          >
            Continue
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
});

export default SessionCompleteOverlay;
